import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, RefreshCw, Share2, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { ShareableResultCard } from '../components/Analysis/ShareableResultCard';
import { SocialShareModal } from '../components/Analysis/SocialShareModal';
import { SharePromptPopup } from '../components/Analysis/SharePromptPopup';
import { ReferralBanner } from '../components/Referral/ReferralBanner';
import { ForensicResultHeader } from '../components/Analysis/Report/ForensicResultHeader';
import { DetectedPatternGrid } from '../components/Analysis/Report/DetectedPatternGrid';
import { ReportUnlockCard, type PaymentReturnNotice } from '../components/Analysis/Report/ReportUnlockCard';
import { ConversationTranscript } from '../components/Analysis/Report/ConversationTranscript';
import { LockedEvidencePreview } from '../components/Analysis/Report/LockedEvidencePreview';
import { RecommendationsPanel } from '../components/Analysis/Report/RecommendationsPanel';
import { PdfReportPreview } from '../components/Analysis/Report/PdfReportPreview';
import { NeedHelpRespondingCard } from '../components/Analysis/Report/NeedHelpRespondingCard';
import { UnlockedBanner } from '../components/Analysis/Report/UnlockedBanner';
import { DetailedFindings } from '../components/Analysis/Report/DetailedFindings';
import { EvidenceBreakdown } from '../components/Analysis/Report/EvidenceBreakdown';
import { ReportTabs, type ReportTab } from '../components/Analysis/Report/ReportTabs';
import { MobileUnlockBar } from '../components/Analysis/Report/MobileUnlockBar';
import { DeleteScanDialog } from '../components/Analysis/Report/DeleteScanDialog';
import { ReportErrorState, ReportFailedState, ReportLoadingState, ReportProcessingState, classifyReportError } from '../components/Analysis/Report/ReportStates';
import { useScan, deleteScan } from '../hooks/useScans';
import { useReportUnlock } from '../hooks/useReportUnlock';
import { useReportSharing } from '../hooks/useReportSharing';
import { useScanCredits } from '../hooks/useScanCredits';
import { useEngagementTimer } from '../hooks/useEngagementTimer';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { useSmartFeedback } from '../contexts/SmartFeedbackContext';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { trackAnalysisViewed } from '../utils/analytics';
import { trackEventOnce } from '../utils/visitorTracking';
import { getConversationText, getFindings, getMessageCount, getReportMessages, getReportRecommendations, hasFullReportAccess, normalizePatterns, resolveRiskLevel } from '../utils/scanReport';
import type { Pattern } from '../types/scan';
const SHARE_PROMPT_PENDING_KEY = 'first_scan_share_prompt_pending';
export function AnalysisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('id');
  // Back from checkout — triggers confirmation polling only, never an unlock.
  const [returnedFromCheckout] = useState(() => searchParams.get('checkout') === 'return');
  // Provider reported the checkout as interrupted.
  const [paymentNotice] = useState<PaymentReturnNotice | null>(() => {
    const value = searchParams.get('payment');
    return value === 'cancelled' || value === 'failed' || value === 'expired' ? value : null;
  });
  const {
    scan,
    loading,
    error,
    refetch,
    statusChange,
    clearStatusChange
  } = useScan(scanId || undefined);
  const {
    user
  } = useAuth();
  // is_pro is read from the user's profile row.
  const {
    isPro,
    loading: entitlementLoading
  } = useScanCredits();
  const {
    showToast
  } = useToast();
  const {
    isOpen: upgradeModalOpen
  } = useUpgradeModal();
  const {
    isOpen: feedbackOpen,
    firstScanPromptPending,
    releaseFirstScanPrompt
  } = useSmartFeedback();

  // Access: profile.is_pro || scan.is_unlocked. Credits never decide access.
  const hasFullAccess = hasFullReportAccess(isPro, scan);
  const sharing = useReportSharing(scan, hasFullAccess);
  const [activeTab, setActiveTab] = useState<ReportTab>('patterns');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUnlockedBanner, setShowUnlockedBanner] = useState(false);
  const [unlockCardInView, setUnlockCardInView] = useState(false);
  const unlockCardRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const score = scan?.score || 0;
  const riskLevel = scan ? resolveRiskLevel(scan) : 'low';

  // Strip checkout params so a refresh reads unlock state from the database only.
  useEffect(() => {
    if (!searchParams.has('checkout') && !searchParams.has('payment')) return;
    const next = new URLSearchParams();
    if (scanId) next.set('id', scanId);
    setSearchParams(next, {
      replace: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleUnlocked = useCallback(() => {
    setShowUnlockedBanner(true);
    setActiveTab('evidence');
    showToast('Forensic report unlocked.', 'success');
    window.setTimeout(() => {
      reportRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 250);
  }, [showToast]);
  const unlock = useReportUnlock({
    scanId,
    isUnlocked: scan ? scan.is_unlocked === true : null,
    score,
    riskLevel,
    returnedFromCheckout,
    checkoutAbandoned: paymentNotice !== null,
    onUnlocked: handleUnlocked,
    refetch
  });

  // --- First-scan review → share prompts (engagement-gated) ---
  const [sharePromptPending, setSharePromptPending] = useState(() => {
    try {
      return localStorage.getItem(SHARE_PROMPT_PENDING_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  const isResultReady = scan?.status === 'completed';
  const anotherModalOpen = upgradeModalOpen || feedbackOpen || showShareModal || showDeleteConfirm || showSharePrompt;
  useEngagementTimer({
    enabled: Boolean(isResultReady && firstScanPromptPending && !anotherModalOpen),
    totalSeconds: 30,
    focusedSeconds: 15,
    focused: hasFullAccess && activeTab === 'patterns',
    onReached: releaseFirstScanPrompt
  });
  useEngagementTimer({
    enabled: Boolean(isResultReady && sharePromptPending && !firstScanPromptPending && !anotherModalOpen),
    totalSeconds: 20,
    onReached: () => {
      localStorage.removeItem(SHARE_PROMPT_PENDING_KEY);
      setSharePromptPending(false);
      setShowSharePrompt(true);
    }
  });
  useEffect(() => {
    const handleFirstFeedbackClosed = () => setSharePromptPending(true);
    window.addEventListener('firstScanFeedbackClosed', handleFirstFeedbackClosed);
    return () => window.removeEventListener('firstScanFeedbackClosed', handleFirstFeedbackClosed);
  }, []);

  // Landing directly on a freshly finished scan still counts toward milestones.
  useEffect(() => {
    if (!scan || scan.status !== 'completed' || !scan.updated_at) return;
    const completionKey = `smart_feedback_completed_scan_${scan.id}`;
    if (localStorage.getItem(completionKey) === 'true') return;
    const ageMs = Date.now() - new Date(scan.updated_at).getTime();
    if (ageMs > 15 * 60 * 1000) return;
    localStorage.setItem(completionKey, 'true');
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('scanCompleted'));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [scan?.id, scan?.status, scan?.updated_at]);

  // Live status transitions while the user waits on this page.
  useEffect(() => {
    if (!statusChange) return;
    const {
      currentStatus,
      scan: changedScan
    } = statusChange;
    if (currentStatus === 'completed') {
      const completionKey = `smart_feedback_completed_scan_${changedScan.id}`;
      if (localStorage.getItem(completionKey) !== 'true') {
        localStorage.setItem(completionKey, 'true');
        requestAnimationFrame(() => {
          window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent('scanCompleted'));
          }, 300);
        });
      }
      showToast(`Analysis complete. Toxicity score ${changedScan.score}/100.`, 'success', 5000);
    } else if (currentStatus === 'failed') {
      showToast('Analysis interrupted. Please try again.', 'error', 6000);
    }
    clearStatusChange();
  }, [statusChange, clearStatusChange, showToast]);

  // Analytics: result viewed (no conversation content).
  useEffect(() => {
    if (!scan || scan.status !== 'completed' || !user?.id) return;
    const timeSinceCompletion = scan.updated_at ? Math.floor((Date.now() - new Date(scan.updated_at).getTime()) / 1000) : 0;
    trackCriticalEvent('Analysis_Viewed', {
      scan_id: scan.id,
      toxicity_score: scan.score || 0,
      risk_level: scan.risk_level || 'unknown',
      patterns_count: Array.isArray(scan.patterns) ? scan.patterns.length : 0,
      is_unlocked: scan.is_unlocked,
      time_since_completion: timeSinceCompletion
    });
    trackAnalysisViewed(scan.risk_level || undefined);
    void trackEventOnce(`result_viewed:${scan.id}`, 'result_viewed', {
      scan_id: scan.id,
      score: scan.score ?? null,
      risk_level: scan.risk_level ?? null,
      is_unlocked: scan.is_unlocked
    });
  }, [scan?.id, scan?.status, user?.id]);

  // Hide the mobile unlock bar while the main unlock card is on screen.
  useEffect(() => {
    const el = unlockCardRef.current;
    if (!el || hasFullAccess) return;
    const observer = new IntersectionObserver(([entry]) => setUnlockCardInView(entry.isIntersecting), {
      threshold: 0.2
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasFullAccess, scan?.id, scan?.status]);

  // --- Handlers ---
  const handleBack = () => navigate('/dashboard');
  const handleUnlock = (location: string) => {
    void unlock.startCheckout(location);
  };
  const handleDelete = async () => {
    if (!scanId) return;
    try {
      setDeleting(true);
      const daysSinceCreation = scan?.created_at ? Math.floor((Date.now() - new Date(scan.created_at).getTime()) / 86_400_000) : 0;
      await trackCriticalEvent('Scan_Deleted', {
        scan_id: scanId,
        toxicity_score: scan?.score || 0,
        days_since_creation: daysSinceCreation,
        was_completed: scan?.status === 'completed'
      });
      await deleteScan(scanId);
      showToast('Case file deleted.', 'success');
      navigate('/dashboard');
    } catch (err) {
      console.error('Error deleting scan:', err);
      showToast('Couldn’t delete this case file. Please try again.', 'error');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  // Scans are read-only from the client, so a failed scan is re-run as a new one.
  const handleRetry = () => {
    if (scanId) {
      void trackCriticalEvent('Scan_Retry_Initiated', {
        scan_id: scanId,
        original_failure_reason: 'user_initiated_retry'
      });
    }
    navigate('/upload');
  };
  const handlePdf = () => {
    if (hasFullAccess) void sharing.handleDownloadPDF();else handleUnlock('pdf_preview');
  };

  // --- Non-report states ---
  if (!scanId) return <ReportErrorState kind="no_id" onBack={handleBack} data-id="element-3433" data-mp="1kpp4pd0z91iu" />;
  if (loading || user && entitlementLoading) return <ReportLoadingState data-id="element-3434" data-mp="1uhuvwkjenewz" />;
  if (error || !scan) return <ReportErrorState kind={classifyReportError(error)} onRetry={refetch} onBack={handleBack} onSignIn={() => navigate('/auth')} data-id="element-3435" data-mp="1ujecydcvxtzt" />;
  if (scan.status === 'processing' || scan.status === 'pending') return <ReportProcessingState onBack={handleBack} data-id="element-3436" data-mp="u0kzcwt0vpu2" />;
  if (scan.status === 'failed') return <ReportFailedState onRetry={handleRetry} onBack={handleBack} data-id="element-3437" data-mp="p1rjwyu6dor9" />;

  // --- Report data ---
  const patterns = normalizePatterns(scan.patterns);
  const findings = getFindings(scan);
  const messageCount = getMessageCount(scan);
  const recommendations = getReportRecommendations(scan);
  const analysisSummary = scan.analysis?.summary || null;
  const rawPatterns = Array.isArray(scan.patterns) ? scan.patterns : [];
  return <div className="relative flex min-h-screen w-full flex-col bg-forensic-bg font-sans text-white" data-id="element-3438" data-mp="63fbxgf02kx9">
      <WebsiteHeader data-id="element-3439" data-mp="l0g1bd9py3sk" />

      <main className="relative z-10 mx-auto w-full max-w-5xl flex-1 px-4 pb-36 pt-6 sm:px-6 md:pb-20 md:pt-10" data-id="element-3440" data-mp="l0xug11341w9">
        <nav className="mb-8 flex items-center justify-between" data-id="element-3441" data-mp="fzrzrxnv64k3">
          <button type="button" onClick={handleBack} className="group inline-flex min-h-[44px] items-center gap-2 text-sm text-forensic-muted transition-colors duration-150 ease-out hover:text-white" data-id="element-3442" data-mp="1wejrs9h3l9sv">
            <ArrowLeft size={16} className="transition-transform duration-150 ease-out group-hover:-translate-x-0.5" data-id="element-3443" data-mp="1z1u6xyhlgw9l" />
            Back to dashboard
          </button>
          <button type="button" onClick={() => setShowDeleteConfirm(true)} aria-label="Delete case file" className="rounded-lg p-2.5 text-forensic-subtle transition-colors duration-150 ease-out hover:text-white" data-id="element-3444" data-mp="1q0e7umw8cgko">
            <Trash2 size={18} data-id="element-3445" data-mp="2mplll8qlxle1" />
          </button>
        </nav>

        <div className="space-y-12 sm:space-y-16" data-id="element-3446" data-mp="2mr6fcw53l0au">
          <div className="space-y-4" data-id="element-3447" data-mp="28wvvot7yjwbn">
            <UnlockedBanner show={showUnlockedBanner && hasFullAccess} onDismiss={() => setShowUnlockedBanner(false)} data-id="element-3448" data-mp="28vujumj4b5sv" />

            <ForensicResultHeader score={score} riskLevel={riskLevel} patternCount={patterns.length} messageCount={messageCount} title={scan.title} createdAt={scan.created_at} summary={hasFullAccess ? analysisSummary : null} data-id="element-3449" data-mp="30obhwm5lrh1c" />
          </div>

          <DetectedPatternGrid patterns={patterns} isUnlocked={hasFullAccess} data-id="element-3450" data-mp="17tc9azvq6x6s" />

          {!hasFullAccess ? <>
              <div ref={unlockCardRef} data-id="element-3451" data-mp="13simk9u0rofh">
                <ReportUnlockCard patternCount={patterns.length} state={unlock.state} error={unlock.error} notice={paymentNotice} onUnlock={() => handleUnlock('result_unlock_card')} onCheckAgain={unlock.checkAgain} data-id="element-3452" data-mp="13tjz6iqooizb" />
              </div>
              <LockedEvidencePreview patterns={patterns} findings={findings} state={unlock.state} onUnlock={() => handleUnlock('evidence_preview')} data-id="element-3453" data-mp="1mrea52bdh4kc" />
              <RecommendationsPanel recommendations={recommendations} isUnlocked={false} onUnlock={() => handleUnlock('recommendations_preview')} data-id="element-3454" data-mp="2eg7nmsae43q8" />
              <PdfReportPreview score={score} riskLevel={riskLevel} patternCount={patterns.length} isUnlocked={false} isGenerating={false} onUnlock={() => handleUnlock('pdf_preview')} onDownload={handlePdf} data-id="element-3455" data-mp="5yhioj5j4jvj" />
            </> : <div ref={reportRef} className="scroll-mt-24" data-id="element-3456" data-mp="ow20555ysg64">
              <ReportTabs active={activeTab} onChange={setActiveTab} onDownloadPdf={handlePdf} isGeneratingPdf={sharing.isGeneratingPDF} data-id="element-3457" data-mp="tnsbnegn19z4" />
              <AnimatePresence mode="wait" initial={false} data-id="element-3458" data-mp="12p0abxn2gctq">
                <motion.div key={activeTab} role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.16,
              ease: [0.23, 1, 0.32, 1]
            }} className="pt-8" data-id="element-3459" data-mp="12mduzbtxot0y">
                  {activeTab === 'patterns' && <DetailedFindings findings={findings} patterns={patterns} data-id="element-3460" data-mp="3mh7goddfe7uk" />}
                  {activeTab === 'evidence' && <EvidenceBreakdown messages={getReportMessages(scan)} hasConversation={Boolean(getConversationText(scan)) || Array.isArray(scan.file_urls) && scan.file_urls.length > 0} onViewConversation={() => setActiveTab('conversation')} data-id="element-3461" data-mp="3ieubbq2lcbyb" />}
                  {activeTab === 'conversation' && <ConversationTranscript conversationText={getConversationText(scan)} fileUrls={Array.isArray(scan.file_urls) ? scan.file_urls : []} data-id="element-3462" data-mp="cjywp0dy10r9" />}
                  {activeTab === 'recommendations' && <RecommendationsPanel recommendations={recommendations} isUnlocked onUnlock={() => undefined} data-id="element-3463" data-mp="7sqlbyt1z5bg" />}
                </motion.div>
              </AnimatePresence>
              <div className="mt-12" data-id="element-3464" data-mp="3g17a981n1eg6">
                <PdfReportPreview score={score} riskLevel={riskLevel} patternCount={patterns.length} isUnlocked isGenerating={sharing.isGeneratingPDF} onUnlock={() => undefined} onDownload={handlePdf} data-id="element-3465" data-mp="3g28yke7nzxfu" />
              </div>
            </div>}

          {/* Secondary + tertiary actions — never louder than the unlock CTA */}
          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-3466" data-mp="1zpf8vrgjgut">
            <button type="button" onClick={() => navigate('/upload')} className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-forensic-line-strong px-6 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-white/30" data-id="element-3467" data-mp="22bt1d4xyslh">
              <RefreshCw size={16} aria-hidden="true" data-id="element-3468" data-mp="3fevq0gezp1oh" />
              Try another scan
            </button>
            <button type="button" onClick={() => setShowShareModal(true)} className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-forensic-muted transition-colors duration-150 ease-out hover:text-white" data-id="element-3469" data-mp="3ah3p4wwda585">
              <Share2 size={16} aria-hidden="true" data-id="element-3470" data-mp="13q47s0meal4c" />
              Share result
            </button>
          </div>

          <NeedHelpRespondingCard data-id="element-3471" data-mp="1b8u0eggzm1gy" />

          <ReferralBanner tone="dark" data-id="element-3472" data-mp="1g7x705cm8fr1" />

          <footer className="border-t border-forensic-line pt-8 text-center" data-id="element-3473" data-mp="1g6vr9nknn52z">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-3474" data-mp="1kyna1bxkk6ik">
              ToxicDetector.ai · Forensic Analysis Unit
            </p>
            <p className="mx-auto mt-2 max-w-lg text-xs leading-relaxed text-forensic-subtle" data-id="element-3475" data-mp="sa9dz1q1jk9n">
              This analysis does not diagnose intent or mental health conditions.
              For professional support, please consult a licensed therapist.
            </p>
          </footer>
        </div>
      </main>

      {!hasFullAccess && <MobileUnlockBar visible={!unlockCardInView && !anotherModalOpen} state={unlock.state} error={unlock.error} onUnlock={() => handleUnlock('mobile_sticky_bar')} data-id="element-3476" data-mp="178a39cweyxut" />}

      <SharePromptPopup isOpen={showSharePrompt} onClose={() => setShowSharePrompt(false)} onDownloadPDF={() => {
      setShowSharePrompt(false);
      handlePdf();
    }} onDownloadImage={() => {
      setShowSharePrompt(false);
      void sharing.handleDownloadImage();
    }} onOpenShareOptions={() => {
      setShowSharePrompt(false);
      setShowShareModal(true);
    }} isGeneratingPDF={sharing.isGeneratingPDF} isGeneratingImage={sharing.isGeneratingImage} data-id="element-3477" data-mp="2ikugyshciay3" />

      <SocialShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} shareAspectRatio={sharing.shareAspectRatio} onAspectRatioChange={sharing.setShareAspectRatio} onSocialShare={sharing.handleSocialShare} onDownloadImage={sharing.handleDownloadImage} onDownloadPDF={() => {
      if (!hasFullAccess) setShowShareModal(false);
      handlePdf();
    }} pdfLocked={!hasFullAccess} isGeneratingImage={sharing.isGeneratingImage} isGeneratingPDF={sharing.isGeneratingPDF} score={score} riskLevel={riskLevel} messageCount={messageCount} patterns={rawPatterns} summary={hasFullAccess ? analysisSummary : null} shareCardTheme={sharing.shareCardTheme} onThemeChange={sharing.setShareCardTheme} data-id="element-3478" data-mp="3f291nodb6ztx" />

      <DeleteScanDialog open={showDeleteConfirm} deleting={deleting} onCancel={() => setShowDeleteConfirm(false)} onConfirm={handleDelete} data-id="element-3479" data-mp="up41ulwm6qx4" />

      {/* Off-screen capture targets for image export */}
      <div className="pointer-events-none fixed -left-[9999px] top-0" aria-hidden="true" data-id="element-3480" data-mp="krzewjuy2rf9">
        <div ref={sharing.squareCaptureRef} className={`h-[1080px] w-[1080px] ${sharing.shareCardTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]'}`} data-id="element-3481" data-mp="gq4fxm0hkq4n">
          <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={messageCount} topPatterns={rawPatterns.slice(0, 3) as Pattern[]} variant="square" summary={hasFullAccess ? analysisSummary || undefined : undefined} theme={sharing.shareCardTheme} data-id="element-3482" data-mp="2b3n8sijknkjm" />
        </div>
        <div ref={sharing.storyCaptureRef} className={`h-[1920px] w-[1080px] ${sharing.shareCardTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]'}`} data-id="element-3483" data-mp="3vjblketwx14o">
          <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={messageCount} topPatterns={rawPatterns.slice(0, 4) as Pattern[]} variant="vertical" summary={hasFullAccess ? analysisSummary || undefined : undefined} theme={sharing.shareCardTheme} data-id="element-3484" data-mp="r7zkjmywrgsl" />
        </div>
      </div>
    </div>;
}